/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from '@mui/material';
import { Form } from 'react-final-form';
import { map } from 'lodash';
import { useSnackbar } from 'notistack';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import {
  validateCombinedAddressLength,
  validateMaxLength,
  validateMinLength,
  validatePhoneNumber,
  validateValueWithRegex,
  validateZipCode,
} from 'common/forms/formValidations';
import { useDispatch } from 'react-redux';
import { editExternalTransferPharmacy } from 'store/thunks/pharmacyThunks';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import { formatZipCode } from 'common/utils';

const EditExternalTransferPharmacyDialog = ({ open, onClose, pharmacy }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const handleEditExternalTransferPharmacy = (formData) => {
    setLoading(true);
    const { pharmacistName, pharmacistNpi, addressLine2, dea, ...restFormData } = formData;
    const pharmacistDetails = {
      ...(pharmacistName && { name: pharmacistName }),
      ...(pharmacistNpi && { npi: pharmacistNpi }),
    };

    const input = {
      pharmacyId: pharmacy.pharmacyId,
      ...restFormData,
      addressLine2: addressLine2 || undefined,
      dea: dea || undefined,
      pharmacistDetails,
    };

    dispatch(editExternalTransferPharmacy(input))
      .then((response) => {
        if (response) {
          enqueueSnackbar('External transfer pharmacy updated successfully', {
            variant: 'success',
          });
          onClose();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const initialFormValues = useMemo(
    () => ({
      pharmacyName: pharmacy?.pharmacyName || '',
      addressLine1: pharmacy?.addressLine1 || '',
      addressLine2: pharmacy?.addressLine2 || '',
      city: pharmacy?.city || '',
      state: pharmacy?.state || '',
      zip: pharmacy?.zip || '',
      phoneNumber: pharmacy?.phone || '',
      fax: pharmacy?.fax || '',
      npi: pharmacy?.npi || '',
      dea: pharmacy?.dea || '',
      pharmacistName: pharmacy?.pharmacistName || pharmacy?.pharmacistDetails?.name || '',
      pharmacistNpi: pharmacy?.pharmacistNpi || pharmacy?.pharmacistDetails?.npi || '',
    }),
    [pharmacy]
  );

  if (!pharmacy) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      aria-describedby='ExternalTransferPharmacy-EditDialog-header'
      fullWidth
      maxWidth='md'
    >
      <DialogTitle id='ExternalTransferPharmacy-EditDialog-header'>
        Edit External Transfer Pharmacy
      </DialogTitle>

      <Form
        initialValues={initialFormValues}
        onSubmit={handleEditExternalTransferPharmacy}
        render={({ handleSubmit, invalid }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <Typography variant='h6' sx={{ mb: 2, mt: 1, fontWeight: 600 }}>
                Pharmacy Information
              </Typography>

              <TextField
                id='ExternalTransferPharmacy-EditDialog-pharmacyName-input'
                label='Pharmacy Name'
                name='pharmacyName'
                required
                autoFocus
                validations={[
                  validateMinLength('Pharmacy Name must be at least 3 characters', 3),
                  validateMaxLength('Maximum 50 characters', 50),
                ]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-addressLine1-input'
                label='Address Line 1'
                name='addressLine1'
                required
                validations={[
                  validateMinLength('Address Line 1 must be at least 3 characters', 3),
                  validateCombinedAddressLength(),
                ]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-addressLine2-input'
                label='Address Line 2'
                name='addressLine2'
                validations={[validateCombinedAddressLength()]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-city-input'
                label='City'
                name='city'
                required
                validations={[validateMinLength('City must be at least 3 characters', 3)]}
              />

              <SelectField
                id='ExternalTransferPharmacy-EditDialog-state-dropdown'
                label='State'
                name='state'
                required
              >
                {map(usStateAbbreviation, ({ name, abbreviation }) => (
                  <MenuItem value={abbreviation} key={abbreviation}>
                    {name}
                  </MenuItem>
                ))}
              </SelectField>

              <TextField
                id='ExternalTransferPharmacy-EditDialog-zip-input'
                label='ZIP Code'
                name='zip'
                type='zip'
                required
                validations={[validateZipCode()]}
                format={formatZipCode}
                maxLength={10}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-phoneNumber-input'
                label='Phone Number'
                name='phoneNumber'
                required
                validations={[validatePhoneNumber()]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-fax-input'
                label='Fax Number'
                name='fax'
                required
                validations={[validatePhoneNumber('Invalid Fax Number')]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-npi-input'
                label='Pharmacy NPI'
                name='npi'
                required
                validations={[validateValueWithRegex('Must be 10 digits', /^\d{10}$/)]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-dea-input'
                label='DEA Number'
                name='dea'
              />

              <Typography variant='h6' sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                Pharmacist Information
              </Typography>

              <TextField
                id='ExternalTransferPharmacy-EditDialog-pharmacistName-input'
                label='Pharmacist Name'
                name='pharmacistName'
                validations={[
                  validateMinLength('Pharmacist Name must be at least 3 characters', 3),
                ]}
              />

              <TextField
                id='ExternalTransferPharmacy-EditDialog-pharmacistNpi-input'
                label='Pharmacist NPI'
                name='pharmacistNpi'
                validations={[validateValueWithRegex('Must be 10 digits', /^\d{10}$/)]}
              />
            </DialogContent>

            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                Cancel
              </Button>
              <LoadingButton loading={loading} variant='contained' disabled={invalid} type='submit'>
                Save
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

EditExternalTransferPharmacyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pharmacy: PropTypes.shape({
    pharmacyId: PropTypes.string.isRequired,
    pharmacyName: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    npi: PropTypes.string,
    dea: PropTypes.string,
    pharmacistName: PropTypes.string,
    pharmacistNpi: PropTypes.string,
    pharmacistDetails: PropTypes.shape({
      name: PropTypes.string,
      npi: PropTypes.string,
    }),
  }).isRequired,
};

export default EditExternalTransferPharmacyDialog;
